// IntercomArticleModal.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { format, fromUnixTime } from 'date-fns';
import { sanitize } from 'isomorphic-dompurify';
import { withTranslation } from 'react-i18next';

import ModalComponent, {
  ModalHeader,
  ModalBody,
} from '../component/ModalComponent.jsx';

import { ModalId, ButtonId } from '../resource/trackingConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import { color } from '../style/variables.js';
import ResetHeadingStyle from '../style/ResetHeadingStyle.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';

import CloseIconSource from '../../img/ic_close_grey.svg';

const MIXPANEL_MODAL_ID = ModalId.Popup.ModalIntercomArticle;

export class IntercomArticleModal extends React.PureComponent {
  nextTick = undefined;
  fetchFaqArticleDetail = () => {
    const { articleId, title, language, fetchFaqArticleDetail } = this.props;
    if (articleId && !title) {
      this.nextTick = setTimeout(() => {
        fetchFaqArticleDetail({ id: articleId, language });
      });
    }
  };
  handleClickClose = () => {
    const { toggleModal } = this.props;
    toggleModal({
      modalId: 'IntercomArticleModal',
      isOpen: false,
    });
  };
  componentDidMount() {
    const { articleConfigKey, setModalTracking } = this.props;
    this.fetchFaqArticleDetail();
    setModalTracking({
      modalId: MIXPANEL_MODAL_ID,
      extraProps: {
        'article.config_key': articleConfigKey,
      },
    });
  }
  componentDidUpdate(prevProps) {
    const { articleId } = this.props;
    if (articleId && articleId !== prevProps.articleId) {
      this.fetchFaqArticleDetail();
    }
  }
  componentWillUnmount() {
    this.nextTick && clearTimeout(this.nextTick);
  }
  render() {
    const { status, title, body, updatedAtUnix, t, articleConfigKey } =
      this.props;
    const trackingPayload = {
      modalId: MIXPANEL_MODAL_ID,
      'article.config_key': articleConfigKey,
    };
    return (
      <StyledIntercomArticleModal
        status={status}
        trackingPayload={trackingPayload}
        onCloseModal={this.handleClickClose}
        isClosable
      >
        <Header>
          <Title>{title}</Title>
          <CloseButton
            data-element_id={ButtonId.All.ButtonClose}
            data-tracking_payload={trackingPayload}
            onClick={this.handleClickClose}
          >
            <img
              src={CloseIconSource}
              alt={t('general_close', {
                ns: TranslationNamespace.GENERAL,
              })}
              title={t('general_close', {
                ns: TranslationNamespace.GENERAL,
              })}
            />
          </CloseButton>
        </Header>
        {body && (
          <Body>
            {updatedAtUnix && (
              <UpdateDate>
                {format(fromUnixTime(updatedAtUnix), 'yyyy/MM/dd HH:mm')}
              </UpdateDate>
            )}
            <Description dangerouslySetInnerHTML={{ __html: sanitize(body) }} />
          </Body>
        )}
      </StyledIntercomArticleModal>
    );
  }
}

IntercomArticleModal.propTypes = {
  status: PropTypes.string,
  t: PropTypes.func.isRequired,
  toggleModal: PropTypes.func,
  fetchFaqArticleDetail: PropTypes.func,
  language: PropTypes.string,
  articleId: PropTypes.string,
  articleConfigKey: PropTypes.string,
  title: PropTypes.string,
  body: PropTypes.string,
  updatedAtUnix: PropTypes.number,
  setModalTracking: PropTypes.func.isRequired,
};

IntercomArticleModal.defaultProps = {
  status: 'display',
  toggleModal: () => null,
  fetchFaqArticleDetail: () => null,
  language: 'zh-TW',
  articleId: null,
  articleConfigKey: null,
  title: null,
  body: null,
  updatedAtUnix: null,
};

const StyledIntercomArticleModal = styled(ModalComponent)``;

const Header = styled(ModalHeader)`
  margin-bottom: 16px;
  ${media.mobile`
    display: flex;
    align-items: center;
  `};
`;

const Title = styled.h4`
  ${ResetHeadingStyle};
  padding-left: 40px;
  padding-right: 40px;
  flex: auto;
  font-size: 28px;
  font-weight: 600;
  color: ${color.neutral[0]};
  line-height: 1.4;
  text-align: left;
  ${media.mobile`
    padding-left: 0px;
    padding-right: 0px;
    font-size: 18px;
  `};
`;

const CloseButton = styled.button`
  ${ResetButtonStyle};
  display: none;
  ${media.mobile`
    height: 32px;
    width: 32px;
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    > img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  `};
`;

const UpdateDate = styled.div`
  margin-bottom: 24px;
  font-size: 16px;
  line-height: 1.4;
  color: ${color.neutral[30]};
  ${media.mobile`
    margin-bottom: 16px;
    font-size: 12px;
  `};
`;

const Body = styled(ModalBody)`
  padding-left: 60px;
  padding-right: 60px;
  color: ${color.neutral[30]};
  &::-webkit-scrollbar {
    display: none;
  }
  ${media.mobile`
    padding-left: 16px;
    padding-right: 16px;
  `};
`;

const Description = styled.div`
  width: 100%;
  height: 100%;
  h3 {
    ${ResetHeadingStyle};
    font-size: 20px;
    font-weight: 600;
    line-height: 1.4;
    color: ${color.neutral[30]};
  }
  p {
    ${ResetHeadingStyle};
    margin-bottom: 16px;
    font-size: 16px;
    line-height: 1.4;
    color: ${color.neutral[30]};
    :last-child {
      margin-bottom: 0px;
    }
  }
  img {
    display: block;
    margin: 0 auto;
    max-width: 80%;
  }
  ${media.mobile`
    h3 {
      font-size: 14px;
    }
    p {
      font-size: 12px;
    }
  `};
`;

export default withTranslation()(IntercomArticleModal);
